import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { Subject, take, takeUntil } from 'rxjs';
import store from 'store2';

import { StaffTimecardService } from '../../../services/staff-timecard.service';
import { StaffScheduleService } from '../../../services/staff-schedule.service';
import { TimecardPdfReportService } from '../../../services/timecard-pdf-report.service';
import { ToastHelper } from '../../common/toaster.helper';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import {
  StaffTimecardDailyView,
  StaffTimecard,
  formatTimeFromString,
  formatDateTimeToTime,
  getClockStatusClass,
  getClockStatusLabel
} from '../../../models/staff-timecard.model';
import { CompanyClinicService } from 'src/app/services/companyClinic.service';

@Component({
  selector: 'app-timecard-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PaginatorComponent],
  templateUrl: './timecard-management.component.html',
  styleUrl: './timecard-management.component.scss'
})
export class TimecardManagementComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data
  dailyViewData: StaffTimecardDailyView[] = [];
  branches: any[] = [];

  // Filters
  selectedDate: string = '';
  selectedBranchId: number | null = null;
  companyId: number = 0;

  // State
  isLoading: boolean = false;

  // Modal state
  showTimecardModal: boolean = false;
  showScheduleModal: boolean = false;
  selectedStaff: StaffTimecardDailyView | null = null;
  modalMode: 'create' | 'edit' = 'create';

  // Report modal state
  showReportModal: boolean = false;
  reportFromDate: string = '';
  reportToDate: string = '';
  reportStaffFilter: string = 'all'; // 'all' or specific userID
  staffListForReport: { userID: number; staffName: string }[] = [];
  isGeneratingReport: boolean = false;

  // Timecard form
  timecardForm: FormGroup = new FormGroup({
    workDate: new FormControl(''),
    clockInTime: new FormControl(''),
    clockOutTime: new FormControl(''),
    notes: new FormControl(''),
    // Optional schedule override for historical entries
    scheduledStartTime: new FormControl(''),
    scheduledEndTime: new FormControl('')
  });

  constructor(
    private timecardService: StaffTimecardService,
    private scheduleService: StaffScheduleService,
    private pdfReportService: TimecardPdfReportService,
    private _companyClinicservice: CompanyClinicService,
    private toastr: ToastHelper
  ) { }

  ngOnInit(): void {
    this.companyId = parseInt(store('companyID') || '0', 10);
    this.selectedDate = this.formatDateForInput(new Date());
    this.loadBranches();
    this.loadDailyView();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============ Data Loading ============

  loadBranches(): void {
    // Get branches from store or API
    const userBranches = store('userBranches');
    this.GetClinicOfficesByCompany(store('companyID')).then((clinicOfficeItems: any) => {
      this.delay(1000).then(() => {
        let allowedBranch: any[] = store('userBranches').split(',');
        if (allowedBranch.length > 0) {
          this.branches = clinicOfficeItems.filter(x => allowedBranch.includes(x.branchID.toString()));
          this.selectedBranchId = parseInt(allowedBranch[0]);
          this.selectedBranchId = userBranches;
        }
      });
    });
  }

  delay(time) {
    return new Promise(resolve => setTimeout(resolve, time));
  }

  GetClinicOfficesByCompany(companyID: number) {
      return new Promise((resolve, reject) => {
        this._companyClinicservice.GetClinicOfficesByCompany(companyID).pipe(take(1)).subscribe(
          (res: any) => {
            var clinicOfficeData = JSON.parse(res.data);
            console.log("data", clinicOfficeData);
            var clinicOfficeItems: any[] = [];
            if (clinicOfficeData.length > 0) {
              clinicOfficeData.forEach(item => {
                var clinicOfficeItem = {
                  "branchID": item.BranchID,
                  "branchName": item.BranchName,
                  "contact": item.ContactNumber,
                  "address": item.AddressLine1 + ", " + item.City
                }
                clinicOfficeItems.push(clinicOfficeItem);
              })
            }
            resolve(clinicOfficeItems);
          },
          err => {
            this.toastr.showError('Load clinics Info failed!');
            reject();
          });
      });
    }

  loadDailyView(): void {
    if (!this.companyId || !this.selectedDate) return;

    this.isLoading = true;
    this.timecardService.getDailyView({
      companyID: this.companyId,
      branchID: this.selectedBranchId || undefined,
      workDate: this.selectedDate
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.dailyViewData = data || [];
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to load data');
        this.isLoading = false;
      }
    });
  }

  // ============ Filter Actions ============

  onDateChange(): void {
    this.loadDailyView();
  }

  onBranchChange(): void {
    this.loadDailyView();
  }

  goToToday(): void {
    this.selectedDate = this.formatDateForInput(new Date());
    this.loadDailyView();
  }

  goToPreviousDay(): void {
    const current = new Date(this.selectedDate);
    current.setDate(current.getDate() - 1);
    this.selectedDate = this.formatDateForInput(current);
    this.loadDailyView();
  }

  goToNextDay(): void {
    const current = new Date(this.selectedDate);
    current.setDate(current.getDate() + 1);
    this.selectedDate = this.formatDateForInput(current);
    this.loadDailyView();
  }

  // ============ Clock In/Out Actions ============

  clockIn(staff: StaffTimecardDailyView): void {
    this.isLoading = true;

    // Use selected date (from management view) and current local time
    const now = new Date();
    const localDateTime = this.formatLocalDateTime(this.selectedDate, now);

    this.timecardService.adminClockIn({
      userID: staff.userID,
      companyID: this.companyId,
      branchID: staff.branchID,
      workDate: this.selectedDate,
      clockInTime: localDateTime
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (result.result === 'Success') {
          this.toastr.showSuccess(`${staff.staffName} clocked in successfully`);
          this.loadDailyView();
        } else if (result.result === 'AlreadyClockedIn') {
          this.toastr.showWarning(`${staff.staffName} is already clocked in`);
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to clock in');
        this.isLoading = false;
      }
    });
  }

  clockOut(staff: StaffTimecardDailyView): void {
    this.isLoading = true;

    // Use selected date (from management view) and current local time
    const now = new Date();
    const localDateTime = this.formatLocalDateTime(this.selectedDate, now);

    this.timecardService.adminClockOut({
      userID: staff.userID,
      branchID: staff.branchID,
      workDate: this.selectedDate,
      clockOutTime: localDateTime
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (result.result === 'Success') {
          this.toastr.showSuccess(`${staff.staffName} clocked out successfully`);
          this.loadDailyView();
        } else if (result.result === 'NotClockedIn') {
          this.toastr.showWarning(`${staff.staffName} is not clocked in`);
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to clock out');
        this.isLoading = false;
      }
    });
  }

  // ============ Modal Actions ============

  openNewTimecardModal(): void {
    this.modalMode = 'create';
    this.selectedStaff = null;
    this.timecardForm.reset({
      workDate: this.selectedDate,
      clockInTime: '',
      clockOutTime: '',
      notes: ''
    });
    this.showTimecardModal = true;
  }

  openEditTimecardModal(staff: StaffTimecardDailyView): void {
    if (!staff.timecardID) return;

    this.modalMode = 'edit';
    this.selectedStaff = staff;

    const clockInTime = staff.clockInTime ? this.formatTimeForInput(new Date(staff.clockInTime)) : '';
    const clockOutTime = staff.clockOutTime ? this.formatTimeForInput(new Date(staff.clockOutTime)) : '';

    // Convert scheduled times from "HH:mm:ss" to "HH:mm" for input
    const scheduledStart = staff.scheduledStartTime ? staff.scheduledStartTime.substring(0, 5) : '';
    const scheduledEnd = staff.scheduledEndTime ? staff.scheduledEndTime.substring(0, 5) : '';

    this.timecardForm.patchValue({
      workDate: this.selectedDate,
      clockInTime: clockInTime,
      clockOutTime: clockOutTime,
      notes: staff.notes || '',
      scheduledStartTime: scheduledStart,
      scheduledEndTime: scheduledEnd
    });
    this.showTimecardModal = true;
  }

  /**
   * Open modal to create a new manual timecard for a specific staff member
   */
  openCreateTimecardModal(staff: StaffTimecardDailyView): void {
    this.modalMode = 'create';
    this.selectedStaff = staff;

    // Pre-fill schedule from current schedule if available
    const scheduledStart = staff.scheduledStartTime ? staff.scheduledStartTime.substring(0, 5) : '';
    const scheduledEnd = staff.scheduledEndTime ? staff.scheduledEndTime.substring(0, 5) : '';

    this.timecardForm.reset({
      workDate: this.selectedDate,
      clockInTime: '',
      clockOutTime: '',
      notes: '',
      scheduledStartTime: scheduledStart,
      scheduledEndTime: scheduledEnd
    });
    this.showTimecardModal = true;
  }

  closeTimecardModal(): void {
    this.showTimecardModal = false;
    this.selectedStaff = null;
  }

  saveTimecard(): void {
    if (!this.timecardForm.valid) return;

    const formValue = this.timecardForm.value;
    const workDate = new Date(formValue.workDate);

    // Validate clock-in time is required
    if (!formValue.clockInTime) {
      this.toastr.showWarning('Clock-in time is required');
      return;
    }

    // Build datetime from date + time
    const clockInDateTime = this.combineDateTime(workDate, formValue.clockInTime);
    const clockOutDateTime = formValue.clockOutTime ? this.combineDateTime(workDate, formValue.clockOutTime) : null;

    // Build schedule times (convert "HH:mm" to "HH:mm:ss" format for API)
    const scheduledStartTime = formValue.scheduledStartTime ? formValue.scheduledStartTime + ':00' : undefined;
    const scheduledEndTime = formValue.scheduledEndTime ? formValue.scheduledEndTime + ':00' : undefined;

    if (this.modalMode === 'create' && this.selectedStaff) {
      // Create new manual timecard
      this.isLoading = true;
      this.timecardService.createTimecard({
        userID: this.selectedStaff.userID,
        companyID: this.companyId,
        branchID: this.selectedStaff.branchID,
        workDate: formValue.workDate,
        clockInTime: clockInDateTime,
        clockOutTime: clockOutDateTime,
        notes: formValue.notes,
        scheduledStartTime: scheduledStartTime,
        scheduledEndTime: scheduledEndTime
      }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (result) => {
          if (result.result === 'Success') {
            this.toastr.showSuccess(`Timecard created for ${this.selectedStaff?.staffName}`);
            this.closeTimecardModal();
            this.loadDailyView();
          } else if (result.result === 'AlreadyExists') {
            this.toastr.showWarning('A timecard already exists for this date');
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.toastr.showError(error.message || 'Failed to create timecard');
          this.isLoading = false;
        }
      });
    } else if (this.modalMode === 'edit' && this.selectedStaff?.timecardID) {
      this.isLoading = true;
      this.timecardService.updateTimecard({
        timecardID: this.selectedStaff.timecardID,
        clockInTime: clockInDateTime,
        clockOutTime: clockOutDateTime,
        notes: formValue.notes
      }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (result) => {
          if (result.success) {
            this.toastr.showSuccess('Timecard updated successfully');
            this.closeTimecardModal();
            this.loadDailyView();
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.toastr.showError(error.message || 'Failed to update timecard');
          this.isLoading = false;
        }
      });
    }
  }

  deleteTimecard(staff: StaffTimecardDailyView): void {
    if (!staff.timecardID) return;

    if (!confirm(`Are you sure you want to delete the timecard for ${staff.staffName}?`)) {
      return;
    }

    this.isLoading = true;
    this.timecardService.deleteTimecard(staff.timecardID).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (result.success) {
          this.toastr.showSuccess('Timecard deleted successfully');
          this.loadDailyView();
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to delete timecard');
        this.isLoading = false;
      }
    });
  }

  // ============ Helper Methods ============

  formatDateForInput(date: Date): string {
    // Use local date components to avoid UTC conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatTimeForInput(date: Date): string {
    return date.toTimeString().slice(0, 5);
  }

  formatLocalDateTime(dateStr: string, time: Date): string {
    // Combine selected date with current time for clock-in/out
    const hours = String(time.getHours()).padStart(2, '0');
    const minutes = String(time.getMinutes()).padStart(2, '0');
    const seconds = String(time.getSeconds()).padStart(2, '0');
    return `${dateStr}T${hours}:${minutes}:${seconds}`;
  }

  combineDateTime(date: Date, time: string): string {
    const [hours, minutes] = time.split(':').map(Number);
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    // Format as local datetime string (YYYY-MM-DDTHH:mm:ss) to avoid UTC conversion
    const year = combined.getFullYear();
    const month = String(combined.getMonth() + 1).padStart(2, '0');
    const day = String(combined.getDate()).padStart(2, '0');
    const h = String(combined.getHours()).padStart(2, '0');
    const m = String(combined.getMinutes()).padStart(2, '0');
    const s = String(combined.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${h}:${m}:${s}`;
  }

  formatScheduledTime(time: string | undefined): string {
    return formatTimeFromString(time);
  }

  formatActualTime(dateTime: Date | string | undefined): string {
    return formatDateTimeToTime(dateTime);
  }

  getStatusClass(status: string): string {
    return getClockStatusClass(status);
  }

  getStatusLabel(status: string): string {
    return getClockStatusLabel(status);
  }

  /**
   * Check if clock-in should be allowed for this staff entry
   * With multi clock-in/out: disabled if user has ANY active session (across all rows)
   */
  canClockIn(staff: StaffTimecardDailyView): boolean {
    // Use hasActiveSession flag - if user has any unclosed session, disable clock-in on ALL their rows
    return !staff.hasActiveSession;
  }

  /**
   * Check if clock-out should be allowed for this staff entry
   * Only allowed on the row that is currently clocked in (has unclosed session)
   */
  canClockOut(staff: StaffTimecardDailyView): boolean {
    // Only enable on the specific row that is clocked in
    return staff.clockStatus === 'ClockedIn';
  }

  /**
   * Get display label for session info
   */
  getSessionLabel(staff: StaffTimecardDailyView): string {
    if (!staff.sessionNumber || staff.sessionNumber === 0) return '';
    if ((staff.totalSessions || 0) <= 1) return '';
    return `Session ${staff.sessionNumber} of ${staff.totalSessions}`;
  }

  /**
   * Get display label for total day hours
   */
  getTotalDayHoursLabel(staff: StaffTimecardDailyView): string {
    if (!staff.totalDayHours || staff.totalDayHours === 0) return '';
    if ((staff.totalSessions || 0) <= 1) return '';
    return `Total: ${staff.totalDayHours.toFixed(2)}h`;
  }

  getDisplayDate(): string {
    if (!this.selectedDate) return '';
    const date = new Date(this.selectedDate);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // ============ PDF Report Methods ============

  openReportModal(): void {
    // Set default date range (current month)
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    this.reportFromDate = this.formatDateForInput(firstDayOfMonth);
    this.reportToDate = this.formatDateForInput(lastDayOfMonth);
    this.reportStaffFilter = 'all';

    // Build staff list from current daily view data
    this.staffListForReport = this.dailyViewData
      .map(s => ({ userID: s.userID, staffName: s.staffName }))
      .filter((item, index, self) =>
        index === self.findIndex(t => t.userID === item.userID)
      )
      .sort((a, b) => a.staffName.localeCompare(b.staffName));

    this.showReportModal = true;
  }

  closeReportModal(): void {
    this.showReportModal = false;
  }

  generatePdfReport(): void {
    if (!this.reportFromDate || !this.reportToDate) {
      this.toastr.showWarning('Please select date range');
      return;
    }

    if (new Date(this.reportFromDate) > new Date(this.reportToDate)) {
      this.toastr.showWarning('From date must be before To date');
      return;
    }

    this.isGeneratingReport = true;

    // Fetch timecards for the selected date range
    const params: any = {
      companyID: this.companyId,
      branchID: this.selectedBranchId || undefined,
      fromDate: this.reportFromDate,
      toDate: this.reportToDate
    };

    // Filter by specific staff if selected
    if (this.reportStaffFilter !== 'all') {
      params.userID = parseInt(this.reportStaffFilter, 10);
    }

    this.timecardService.getTimecards(params).pipe(takeUntil(this.destroy$)).subscribe({
      next: (timecards: StaffTimecard[]) => {
        if (!timecards || timecards.length === 0) {
          this.toastr.showWarning('No timecard data found for the selected period');
          this.isGeneratingReport = false;
          return;
        }

        // Get company and branch names
        const companyName = store('companyName') || 'Company';
        const branchName = this.branches.find(b => b.branchID == this.selectedBranchId)?.branchName || 'N/A';
        const staffFilterDisplay = this.reportStaffFilter === 'all'
          ? 'All Staff'
          : this.staffListForReport.find(s => s.userID === parseInt(this.reportStaffFilter, 10))?.staffName || 'Selected Staff';

        // Generate PDF
        this.pdfReportService.generateReport({
          companyName,
          branchName,
          fromDate: this.reportFromDate,
          toDate: this.reportToDate,
          generatedDate: new Date().toLocaleString(),
          timecards,
          staffFilter: staffFilterDisplay
        });

        this.toastr.showSuccess('PDF report generated successfully');
        this.closeReportModal();
        this.isGeneratingReport = false;
      },
      error: (error) => {
        this.toastr.showError(error.message || 'Failed to fetch timecard data');
        this.isGeneratingReport = false;
      }
    });
  }
}
